<template>
  <div class="content mb-5" ref="security-page">
    <b-row>
      <b-col class="mb-3">
        <h2>{{ this.$t("Change Password") }}</h2>
        <p>
          {{
            $t(
              "It can be difficult to know if someone else is using your account, so by changing your password consistently, you reduce the risk that other people will have frequent access to your account. Consider changing your password every few months for peace of mind."
            )
          }}
        </p>
      </b-col>
    </b-row>
    <b-form class="mb-5" v-if="!loading">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group
            :label="this.$t('Old Password')"
            label-for="old-password"
            :state="$validateState($v.changePassswordForm.old_password)"
          >
            <b-form-input
              data-test-id="profile-security-old-password"
              :state="$validateState($v.changePassswordForm.old_password)"
              id="old-password"
              type="password"
              v-model="changePassswordForm.old_password"
              @blur="setValidationInput('old_password', $event.target.value)"
              :placeholder="$t('Old Password')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.changePassswordForm.old_password.required">
                {{ $t("This is a required field.") }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.old_password.required &&
                  $v.changePassswordForm.old_password.$dirty &&
                  !$v.changePassswordForm.old_password.minLength
                "
              >
                {{
                  $t("Old password must be at least 6 characters long.", {
                    field: $t("Password"),
                    length:
                      $v.changePassswordForm.old_password.$params.minLength.min,
                  })
                }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.old_password.$dirty &&
                  !$v.changePassswordForm.old_password.containsSpecial &&
                  $v.changePassswordForm.old_password.minLength &&
                  $v.changePassswordForm.old_password.required
                "
              >
                {{
                  $t("Password must contain a letter and a number or symbol")
                }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group
            :label="this.$t('New Password')"
            label-for="new-password"
            :state="$validateState($v.changePassswordForm.new_password)"
          >
            <b-form-input
              data-test-id="profile-security-old-password"
              :state="$validateState($v.changePassswordForm.new_password)"
              id="new-password"
              type="password"
              v-model="changePassswordForm.new_password"
              @blur="setValidationInput('new_password', $event.target.value)"
              :placeholder="$t('New Password')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.changePassswordForm.new_password.required">
                {{ $t("This is a required field.") }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.new_password.$dirty &&
                  !$v.changePassswordForm.new_password.minLength
                "
              >
                {{
                  $t("New password must be at least 8 characters long.", {
                    field: $t("Password"),
                    length:
                      $v.changePassswordForm.new_password.$params.minLength.min,
                  })
                }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.new_password.$dirty &&
                  !$v.changePassswordForm.new_password.containsSpecial &&
                  $v.changePassswordForm.new_password.minLength &&
                  $v.changePassswordForm.new_password.required
                "
              >
                {{
                  $t("Password must contain a letter and a number or symbol")
                }}
              </p>
            </template>
          </b-form-group>
          <b-form-group
            :label="this.$t('Re-enter New Password')"
            label-for="new-password-confirmation"
            :state="$validateState($v.changePassswordForm.new_passwordConfirm)"
          >
            <b-form-input
              data-test-id="profile-security-old-password"
              :state="
                $validateState($v.changePassswordForm.new_passwordConfirm)
              "
              id="new-password-confirmation"
              type="password"
              @blur="
                setValidationInput('new_passwordConfirm', $event.target.value)
              "
              v-model="changePassswordForm.new_passwordConfirm"
              :placeholder="$t('Re-enter New Password')"
            />

            <template #invalid-feedback>
              <p v-if="!$v.changePassswordForm.new_passwordConfirm.required">
                {{ $t("This is a required field.") }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.new_passwordConfirm.containsSpecial &&
                  !$v.changePassswordForm.new_passwordConfirm.sameAsPassword &&
                  $v.changePassswordForm.new_passwordConfirm.minLength
                "
              >
                {{ $t("Passwords must match.") }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.new_passwordConfirm.$dirty &&
                  !$v.changePassswordForm.new_passwordConfirm.minLength
                "
              >
                {{
                  $t("New password must be at least 8 characters long.", {
                    field: $t("Password"),
                    length:
                      $v.changePassswordForm.new_passwordConfirm.$params
                        .minLength.min,
                  })
                }}
              </p>
              <p
                v-if="
                  $v.changePassswordForm.new_passwordConfirm.$dirty &&
                  !$v.changePassswordForm.new_passwordConfirm.containsSpecial &&
                  $v.changePassswordForm.new_passwordConfirm.minLength &&
                  $v.changePassswordForm.new_passwordConfirm.required
                "
              >
                {{
                  $t("Password must contain a letter and a number or symbol")
                }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6"> </b-col>
      </b-row>
      <t-save-button
        data-test-id="profile-security-change-password-save"
        :form-model="$v.changePassswordForm"
        :fetching="fetching.changePassword"
        @submit="onChangePasswordSubmit"
      />
    </b-form>
    <b-row>
      <b-col class="mb-3">
        <h2>{{ this.$t("Two-factor Authentication (2FA)") }}</h2>
        <p>
          {{
            $t(
              "Two-factor authentication is an additional layer of security used to protect your account from unauthorized access. If enabled, a user must provide their username, password and a one-time password when logging in."
            )
          }}
        </p>
      </b-col>
    </b-row>
    <b-form class="mb-5" v-if="!loading && !twoFactorAuthEnabled">
      <b-row>
        <b-col cols="12" md="6">
          <label for="qr-code" class="mb-1">{{ $t("QR Code") }}</label>
          <b-card class="qr-code-container">
            <img id="qr-code" :src="twoFactorAuthForm.qrCode" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group
            id="type"
            label-for="otp-secret"
            :label="this.$t('OTP Secret')"
          >
            <b-form-input
              data-test-id="profile-security-otp-secret"
              id="otp-secret"
              v-model="otpSecret"
              readonly
            ></b-form-input>
          </b-form-group>

          <b-form-group id="type" label-for="user" :label="this.$t('User')">
            <b-form-input
              data-test-id="profile-security-2fa-user"
              id="user"
              v-model="authUser.email"
              disabled
            ></b-form-input>
          </b-form-group>

          <b-form-group
            class="mb-3"
            label-for="code"
            :label="this.$t('Code')"
            :state="$validateState($v.twoFactorAuthForm.code)"
          >
            <b-form-input
              id="code"
              data-test-id="profile-security-2fa-code"
              v-model="$v.twoFactorAuthForm.code.$model"
              v-on:keyup.enter="onSubmit"
              :state="$validateState($v.twoFactorAuthForm.code)"
              :placeholder="this.$t('Enter code from your authentication app')"
              :disabled="fetching.twoFactorAuth"
            />

            <template #invalid-feedback>
              <p v-if="!$v.twoFactorAuthForm.code.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6"> </b-col>
      </b-row>

      <t-save-button
        :btn-text="$t('Register')"
        data-test-id="profile-security-2fa-register"
        :form-model="$v.twoFactorAuthForm"
        :fetching="fetching.twoFactorAuth"
        @submit="on2FASubmit"
      />
    </b-form>
    <b-form class="mb-5" v-if="!loading && twoFactorAuthEnabled">
      <b-row>
        <b-col cols="12">
          <b-form-group
            id="type"
            label-for="user"
            :label="this.$t('Two-factor Authentication Status')"
          >
            <b-form-input
              data-test-id="profile-security-2fa-user"
              id="user"
              disabled
              :value="$t('On')"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" class="d-flex justify-content-end">
          <b-button @click="disable2FA" :disabled="fetching.twoFactorAuth">
            <span v-if="!fetching.twoFactorAuth">
              {{ $t("Disable") }}
            </span>
            <span class="px-2" v-else
              ><b-icon icon="arrow-clockwise" animation="spin"
            /></span>
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="this.backupCodes !== null">
        <b-col class="mb-3">
          <h2>{{ this.$t("Recovery Codes") }}</h2>
          <p class="mb-0">
            {{
              $t(
                "Store these codes carefully. They are the only way to access Web Protection if the device you registered 2FA on is lost. These codes are one-time use and must be used in sequential order."
              )
            }}
          </p>
        </b-col>
        <b-col cols="12" class="mb-3">
          <b-card>
            <p v-for="(code, idx) in this.backupCodes" :key="idx" class="mb-0">
              {{ code }}
            </p>
          </b-card>
        </b-col>
        <b-col cols="12" class="d-flex justify-content-end">
          <!--          <b-button class="btn-tertiary" @click="download2faCodes">{{-->
          <!--            $t("Download")-->
          <!--          }}</b-button>-->
          <!--          <b-button class="btn-tertiary" @click="print2faCodes">{{-->
          <!--            $t("Print")-->
          <!--          }}</b-button>-->
          <b-button class="btn-tertiary" @click="copy2faCodes">{{
            $t("Copy")
          }}</b-button>
        </b-col>
      </b-row>
      <b-row v-if="this.backupCodes !== null">
        <b-col class="warning-message">
          <p class="mb-0">
            {{
              $t(
                "Save your recovery codes now! For security reasons the codes above will not be visible after closing this screen"
              )
            }}
          </p>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<route>
{
  "name": "Security"
}
</route>

<script>
import { required, minLength, sameAs } from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";
import qrcode from "qrcode";
import { authenticator } from "otplib";

export default {
  layout: "userprofile",
  data() {
    return {
      loading: true,
      twoFactorAuthEnabled: false,
      fetching: {
        changePassword: false,
        twoFactorAuth: false,
      },
      otpSecret: "",
      changePassswordForm: {
        old_password: "",
        new_password: "",
        new_passwordConfirm: "",
      },
      twoFactorAuthForm: {
        code: "",
      },
    };
  },
  validations: {
    changePassswordForm: {
      old_password: {
        required,
        minLength: minLength(6),
      },
      new_password: {
        required,
        minLength: minLength(8),
        containsSpecial(password) {
          return this.$validPassword(password);
        },
      },
      new_passwordConfirm: {
        required,
        minLength: minLength(8),
        containsSpecial(password) {
          return this.$validPassword(password);
        },
        sameAsPassword: sameAs("new_password"),
      },
    },
    twoFactorAuthForm: {
      code: {
        required,
      },
    },
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("users", [
      "updateCustomerAccount",
      "updateCustomerPassword",
      "fetchCustomerAccount",
    ]),
    ...mapActions("authentication", [
      "registerCustomerForOAuth",
      "enable2fa",
      "disable2fa",
    ]),
    ...mapMutations("authentication", ["setBackupCodes"]),
    setValidationInput(object, value) {
      this.changePassswordForm[`${object}`] = value;
      this.$v.changePassswordForm[`${object}`].$touch();
    },
    generateQRCode() {
      this.otpSecret = authenticator.generateSecret(20);
      const otpauth = authenticator.keyuri(
        this.authUser.email,
        "WTC",
        this.otpSecret
      );

      qrcode.toDataURL(otpauth, (err, imageUrl) => {
        if (err) {
          console.error("Error with QR"); // eslint-disable-line no-console
          return;
        }
        this.twoFactorAuthForm.qrCode = imageUrl;
      });
    },
    async onChangePasswordSubmit() {
      this.fetching.changePassword = true;
      try {
        await this.updateCustomerPassword({
          body: this.changePassswordForm,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
        this.changePassswordForm.old_password = "";
        this.changePassswordForm.new_password = "";
        this.changePassswordForm.new_passwordConfirm = "";
      } finally {
        this.fetching.changePassword = false;
        this.$v.changePassswordForm.$reset();
      }
    },
    async on2FASubmit() {
      this.fetching.twoFactorAuth = true;
      try {
        await this.enable2fa({
          secret: this.otpSecret,
          code: this.twoFactorAuthForm.code,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
        this.twoFactorAuthEnabled = true;
      } finally {
        this.fetching.twoFactorAuth = false;
      }
    },
    async disable2FA() {
      this.fetching.twoFactorAuth = true;
      try {
        await this.disable2fa();
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Disabled"),
          duration: 2000,
          variant: "success",
        });
        this.twoFactorAuthEnabled = false;
        this.generateQRCode();
      } finally {
        this.fetching.twoFactorAuth = false;
        this.$v.twoFactorAuthForm.$reset();
      }
    },
    async download2faCodes() {
      this.displayToast({
        title: this.$t("Error"),
        message: "Missing endpoint.",
        duration: 2000,
        variant: "warning",
      });
    },
    async print2faCodes() {
      const printWindow = window.open("", "PRINT", "height=400,width=600");

      printWindow.document.write(
        `<html><head><title>${this.$t("Recovery Codes")}</title>`
      );
      printWindow.document.write("</head><body >");
      printWindow.document.write(`<h1>${this.$t("Recovery Codes")}</h1>`);
      printWindow.document.write(`<pre>${this.backupCodes}</pre>`);
      printWindow.document.write("</body></html>");

      printWindow.document.close();
      printWindow.focus();

      printWindow.print();
      printWindow.close();
    },
    async copy2faCodes() {
      await navigator.clipboard.writeText(this.backupCodes);
      this.displayToast({
        title: this.$t("Copied"),
        message: this.$t("Copied to clipboard."),
        duration: 2000,
        variant: "info",
      });
    },
  },
  computed: {
    ...mapGetters("authentication", [
      "selectedUser",
      "authUser",
      "backupCodes",
    ]),
    ...mapGetters("users", ["userAccounts"]),
    recoveryCodes() {
      return this.backupCodes;
    },
  },
  async created() {
    window.addEventListener("storage", () => {
      let userPrefs = JSON.parse(window.localStorage.getItem("user_prefs"));
      if (userPrefs && !userPrefs.token) {
        // If token doesn't exist anymore, redirect to the homepage:
        window.location.assign("/");
      }
    });

    this.twoFactorAuthEnabled = this.$userPrefs.get2FAEnabled();
    if (this.twoFactorAuthEnabled === false) {
      this.generateQRCode();
    }
    this.setPageTitle(this.$t("Security"));
    this.setPageSubtitle([
      this.$t(
        "Manage your account security to ensure you are fully protected."
      ),
    ]);
    this.setBackupCodes(null);

    try {
      // await this.registerCustomerForOAuth({
      //   id: this.authUser.id,
      //   email: this.authUser.email,
      // });
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
div.invalid-feedback {
  position: relative;

  p {
    position: relative !important;
    margin: 0;
  }
}
.qr-code-container {
  box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.39);
  justify-content: center;
  align-items: center;
  padding: 0.75rem 0;

  #qr-code {
    width: 170px;
    height: 170px;
  }
}
</style>
